import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { getMonday } from "generated/api/monday";

import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";

import { useCampaign } from "hooks/api/use-campaigns";

import {
  GetGroupItemsPopulationType,
  GetGroupItemsCampaignObjective,
  type GetGroupItemsParams,
} from "generated/schemas";

type WithTransferStudentStatusFlag<TParams> = TParams & {
  transferStudentStatusFlag: boolean;
};

interface UseAvailableCampaignTemplatesParams {
  selectedStrategy: string | undefined;
  campaignObjective: string;
  populationType: string;
}

export const useAvailableCampaignTemplates = ({
  selectedStrategy,
  campaignObjective,
  populationType,
}: UseAvailableCampaignTemplatesParams) => {
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { data: currentCampaign } = useCampaign({ clientId, id: campaignId });
  const { setCampaignTemplates, setIsLoadingCampaignTemplates } =
    useAddTouchpointsFromMondayTemplateContext();
  const transferStudentStatusFlag = currentCampaign?.transferStudentStatusFlag ?? false;

  useEffect(() => {
    if (!selectedStrategy) {
      setCampaignTemplates([]);
      return;
    }

    if (!currentCampaign || !populationType) {
      setCampaignTemplates([]);
      return;
    }

    const fetchAvailableCampaignTemplates = async () => {
      setIsLoadingCampaignTemplates(true);
      try {
        const monday = getMonday();
        const params: WithTransferStudentStatusFlag<GetGroupItemsParams> = {
          populationType: populationType as GetGroupItemsPopulationType,
          campaignObjective: campaignObjective as GetGroupItemsCampaignObjective,
          transferStudentStatusFlag,
        };
        const results = await monday.getGroupItems({ groupId: selectedStrategy }, params);

        setCampaignTemplates(results);
      } catch (error) {
        console.error("Failed to fetch campaign templates", error);
        setCampaignTemplates([]);
      } finally {
        setIsLoadingCampaignTemplates(false);
      }
    };

    fetchAvailableCampaignTemplates();
  }, [
    selectedStrategy,
    populationType,
    campaignObjective,
    currentCampaign,
    transferStudentStatusFlag,
    setCampaignTemplates,
    setIsLoadingCampaignTemplates,
  ]);
};
